/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment library for date and time formatting.
import PropTypes from 'prop-types' // Npm: prop-types library.
import * as XLSX from 'xlsx' // Npm: XLSX library.
import _ from 'underscore' // Npm: utility module.
import { toast } from 'react-hot-toast' // Npm: React hot toast for notifications.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useMutation, useQuery } from '@apollo/client' // Npm: Apollo client for GraphQL queries and mutations.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { HiArrowPath } from 'react-icons/hi2' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI DataGrid component.
import {
  Button,
  Flex,
  Box,
  Checkbox,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import Modal from 'components/Modal'
import TableSpinner from 'components/TableSpinner'
import MncDropDownOptions from 'components/MncDropDownOptions'
import MncUpsert from 'components/MncUpsert'
import MncDelete from 'components/MncDelete'
import FileUpload from 'components/FileUpload'
import { MemoizedInput } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import MncCreateThroughExcelMutation from './__mutation__/index.mnc.createThroughExcel.mutation'
import MncReadQuery from './__query__/index.mnc.read.query'


/*
 * STYLES
 */
import { buttonStyle } from './index.style'
import { TbRulerMeasure } from 'react-icons/tb'

/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Const assignment.
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [selectAll, setSelectAll] = React.useState(false)
  const [selectedIds, setSelectedIds] = React.useState([])
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })

  // Hook assignment - Column visibility model.
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })

  // Hook assignment - Search states.
  const [operatorToSearch, setOperatorToSearch] = React.useState('')
  const [mccToSearch, setMccToSearch] = React.useState('')
  const [mncToSearch, setMncToSearch] = React.useState('')
  const [countryNameToSearch, setCountryNameToSearch] = React.useState('')

  // Hook assignment 
  const [onSuccessfulFileUpload, setOnSuccessfulFileUpload] = React.useState(false)
  const [uploadingFile, setUploadingFile] = React.useState(false)

  // Hook assignment - Modal states.
  const { 'isOpen': isMncCreateOpen, 'onOpen': onMncCreateOpen, 'onClose': onMncCreateClose } = useDisclosure()
  const { 'isOpen': isMncUpdateOpen, 'onOpen': onMncUpdateOpen, 'onClose': onMncUpdateClose } = useDisclosure()
  const { 'isOpen': isMncDeleteOpen, 'onOpen': onMncDeleteOpen, 'onClose': onMncDeleteClose } = useDisclosure()

  // Create MNC through Excel file.
  const [MutationMncCreateThroughExcel] = useMutation(MncCreateThroughExcelMutation)

  const _mncReadVariables = React.useMemo(() => ({
      'mccId': passOn?.mccId,
      operatorToSearch,
      mccToSearch,
      mncToSearch,
      countryNameToSearch,
      'skip': paginationModel.pageSize * paginationModel.page,
      'take': paginationModel.pageSize
  }), [countryNameToSearch, mccToSearch, mncToSearch, operatorToSearch, paginationModel.page, paginationModel.pageSize, passOn?.mccId])
  const _isPreviousPageRef = React.useRef(false)
  const _paginationModelRef = React.useRef(paginationModel)

  //  Fetch MNC data with pagination and search filters.
  const _QueryMncRead = useQuery(MncReadQuery, {
    'variables': _mncReadVariables,
    'fetchPolicy': _isPreviousPageRef.current ? 'cache-first' : Object.React.App.fetchPolicy
  })

  // Ref assignment - Track first load completion and table header height.
  const _isFirstLoadCompleted = React.useRef(false)
  const _rowCountRef = React.useRef(0)
  const _tableHeaderHeightRef = React.useRef(0)

  // Responsive view detection.
  const _isCurrentViewMobile = useBreakpointValue({
    'base': 'false',
    'md': false,
    'lg': false,
    'xl': false,
    'sm': true,
    'xs': true
  })

  // Debounce handlers
  const _onOperatorInputChange = React.useRef(_.debounce((e) => setOperatorToSearch(e.target.value), 800)).current
  const _onMccInputChange = React.useRef(_.debounce((e) => setMccToSearch(e.target.value), 800)).current
  const _onMncInputChange = React.useRef(_.debounce((e) => setMncToSearch(e.target.value), 800)).current
  const _onCountryNameInputChange = React.useRef(_.debounce((e) => setCountryNameToSearch(e.target.value), 800)).current

  // Cleanup on unmount.
  React.useEffect(() => {
    return () => {
      _onOperatorInputChange.cancel?.()
      _onMccInputChange.cancel?.()
      _onMncInputChange.cancel?.()
      _onCountryNameInputChange.cancel?.()
    }
  }, [])

  // Event handler.
  React.useEffect(() => {
    // Reset selectedIds when the data is re-fetched
    if (_QueryMncRead.data) {
      // Reset states.
      setSelectedIds([])
      setSelectAll(false)
    }
  }, [
    isMncCreateOpen,
    isMncUpdateOpen,
    isMncDeleteOpen,
    _QueryMncRead.data
  ])

  // Object assignment.
  const _HandleCheckboxChange = id => {
    // Update state.
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id))
    } else {
      setSelectedIds([...selectedIds, id])
    }
  }

  const _HandleSelectAll = () => {
    // Update state.
    setSelectAll(!selectAll)
    setSelectedIds(selectAll ? [] : _QueryMncRead?.data?.MncRead?.map(item => item.id) || [])
  }

  // Data assignment - Check if first load is completed.
  if (!_QueryMncRead.loading && 0 < _QueryMncRead.data?.MncRead?.length) {
    _isFirstLoadCompleted.current = true
  }

  if (0 === _QueryMncRead.data?.MncRead?.length ||
    (0 < _QueryMncRead.data?.MncRead?.length &&
      !_.every(_.pluck(_QueryMncRead.data?.MncRead, 'status'), i => _successFlags?.includes(i)))) {
    _isFirstLoadCompleted.current = false
  }

  // Event handler.
  const [isRefreshing, setIsRefreshing] = React.useState(false)

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true)

    try {
      await _QueryMncRead.refetch()
    } finally {
      setIsRefreshing(false)
    }
  }, [_QueryMncRead.refetch])
  const refetch = React.useCallback(() => _QueryMncRead.refetch(), [_QueryMncRead.refetch])
  const _HandlePaginationModelChange = React.useCallback(__paginationModel => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize

    _paginationModelRef.current = __paginationModel
    setPaginationModel(__paginationModel)
  }, [])

  // Columns definition.
  const columns = React.useMemo(() => [
    {
      field: 'checkbox',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      renderHeader: () => (
        <Checkbox bg='gray.300' onChange={_HandleSelectAll} isChecked={selectAll} />
      ),
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
          <Checkbox
            bg='gray.300'
            isChecked={selectedIds.includes(row.id)}
            onChange={() => _HandleCheckboxChange(row.id)}
          />
        </Box>
      )
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'sortable': false,
      'filterable': false
    },
    {
      'field': 'operator',
      'headerName': 'Operator',
      'editable': false,
      'sortable': true,
      'filterable': TbRulerMeasure,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 200
    },
    {
      'field': 'mcc',
      'headerName': 'Mcc',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'width': 120
    },
    {
      'field': 'mnc',
      'headerName': 'Mnc',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'width': 120
    },
    {
      'field': 'country',
      'headerName': 'Country',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'createdAt',
      'headerName': 'CreatedAt',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 190
    },
    {
      'field': 'updatedAt',
      'headerName': 'UpdatedAt',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 190
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 100,
      'renderCell': ({ row }) => (
        <MncDropDownOptions
          mncIds={[...selectedIds, row.id]}
          mncId={row.id}
          onMncUpdate={onMncUpdateOpen}
          onMncDelete={onMncDeleteOpen}
          selectedIds={selectedIds}
        />
      )
    }
  ], [selectAll, selectedIds])

  // Rows mapping .
  const rows = React.useMemo(() =>
    _.compact(
      _QueryMncRead.data?.MncRead?.map((item, index) =>
        _successFlags.includes(item?.status)
          ? {
            'id': item?.id ?? `mnc-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
            'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
            'operator': item?.network ?? '-',
            'mcc': item?.Mcc?.mcc ?? '-',
            'mnc': item?.mnc ?? '-',
            'country': item?.Mcc?.countryName ?? '-',
            'createdAt': Moment(item?.createdAt).format('YYYY-MM-DD hh:mm:ss A'),
            'updatedAt': Moment(item?.updatedAt).format('YYYY-MM-DD hh:mm:ss A')
          }
          : null
      )
    ) || [],
    [_QueryMncRead.data?.MncRead, paginationModel.page, paginationModel.pageSize]
  )
  const _rowCount = _.first(_QueryMncRead?.data?.MncRead)?._totalCount
  const rowCount = React.useMemo(() => {
    if (undefined !== _rowCount && null !== _rowCount) _rowCountRef.current = _rowCount

    return _rowCountRef.current
  }, [_rowCount])

  // Return component.
  return (
    <>
      <Flex className="mnc base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "14px"}
          bg="white"
          minH={_isCurrentViewMobile ? "130vh" : "100vh"}
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          {/* Header Section */}
          <Flex
            pb="0px"
            justify="space-between"
            overflow="hidden"
            flexWrap={{ base: "wrap", md: "wrap" }}
          >
            <Flex gap="12px">
              <Tooltip label="Create New Operator" fontSize="sm">
                <Button
                  onClick={onMncCreateOpen}
                  leftIcon={<IoMdAdd />}
                  style={buttonStyle}
                >
                  Create Mnc
                </Button>
              </Tooltip>
            </Flex>
            <Flex gap="12px">
              <Button
                borderRadius="8px"
                fontWeight="450"
                size="sm"
                minW="20px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={<HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                Refresh
              </Button>
              <FileUpload
                uploadingFile={uploadingFile}
                bg={onSuccessfulFileUpload ? "green.100" : void 0}
                color={onSuccessfulFileUpload ? "green" : void 0}
                label={
                  _isCurrentViewMobile
                    ? ""
                    : onSuccessfulFileUpload
                      ? "Upload Successful"
                      : "Upload Mnc"
                }
                onChange={(e) => {
                  // Set uploadingFile to true when file upload begins.
                  setUploadingFile(true);

                  // Promise all file uploads.
                  Promise.all(
                    Array.from(e.target.files).map((file) => {
                      // Const assignment.
                      const _FileReader = new FileReader();

                      // Event listener - Read file as binary string.
                      _FileReader.readAsBinaryString(file);
                      _FileReader.onload = () => {
                        // Const assignment - Parse Excel file.
                        const _data = _FileReader.result;
                        const _workbook = XLSX.read(_data, { type: "binary" });
                        const _sheetName = _workbook.SheetNames[0];
                        const _sheet = _workbook.Sheets[_sheetName];
                        const _parsedData = XLSX.utils.sheet_to_json(_sheet);

                        // Call the mutation to create Mnc through excel.
                        MutationMncCreateThroughExcel({
                          variables: {
                            file: _parsedData,
                            name: file.name,
                          },
                        }).then((e) => {
                          // Set uploadingFile to false when upload completes.
                          setUploadingFile(false);
                          setOnSuccessfulFileUpload(true);

                          // Set a timeout to remove the success message after 3 seconds.
                          const j = setTimeout(() => {
                            setOnSuccessfulFileUpload(false);
                            clearTimeout(j);
                          }, 3000);

                          // Toast the success message.
                          e?.data?.MncCreateThroughExcel?.map(
                            (r) => !_.isEmpty(r?.message) && toast(r?.message),
                          );
                        });
                      };

                      // Return void 0.
                      return void 0;
                    }),
                  );
                }}
                accept=".xlsx, .xls, .csv"
                disabled={uploadingFile}
              />
            </Flex>
          </Flex>

          {/* Filter Section  */}
          <Flex
            gap="12px"
            flexWrap="wrap"
            bg="white"
            p="10px"
            borderRadius="12px"
            border="1px solid #C5CFE8"
            alignItems="flex-start"
          >
            <Box minW="150px" w="200px">
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Operator
              </Text>
              <MemoizedInput
                placeholder="Search by Operator"
                className="filter searchInput"
                defaultValue={operatorToSearch}
                onChange={_onOperatorInputChange}
              />
            </Box>

            <Box minW="150px" w="200px">
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Mcc
              </Text>
              <MemoizedInput
                placeholder="Search by Mcc"
                className="filter searchInput"
                defaultValue={mccToSearch}
                onChange={_onMccInputChange}
              />
            </Box>

            <Box minW="150px" w="200px">
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Mnc
              </Text>
              <MemoizedInput
                placeholder="Search by Mnc"
                className="filter searchInput"
                defaultValue={mncToSearch}
                onChange={_onMncInputChange}
              />
            </Box>

            <Box minW="150px" w="200px">
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Country Name
              </Text>
              <MemoizedInput
                placeholder="Search by Country"
                className="filter searchInput"
                defaultValue={countryNameToSearch}
                onChange={_onCountryNameInputChange}
              />
            </Box>
          </Flex>

          {/* MUI DataGrid */}
          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={48}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={_HandlePaginationModelChange}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            loading={_QueryMncRead?.loading}
          />
        </Flex>
        <Modal
          size="sm"
          title="Mnc Create"
          isOpen={isMncCreateOpen}
          onClose={onMncCreateClose}
        >
          <MncUpsert
            isOpen={isMncCreateOpen}
            isCreateOnly={true}
            onClose={onMncCreateClose}
            refetch={refetch}
          />
        </Modal>
        <Modal
          size="sm"
          title="Mnc Update"
          isOpen={isMncUpdateOpen}
          onClose={onMncUpdateClose}
        >
          <MncUpsert
            isOpen={isMncUpdateOpen}
            onClose={onMncUpdateClose}
            refetch={refetch}
          />
        </Modal>
        <Modal
          size="md"
          title="Mnc Delete"
          isOpen={isMncDeleteOpen}
          onClose={onMncDeleteClose}
        >
          <MncDelete onClose={onMncDeleteClose} refetch={refetch} />
        </Modal>
      </Flex>
    </>
  );
}

/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
